import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  AGGREGATION_LABEL,
  fetchTeamOkrs,
  reviewOkr,
  quarterLabel,
  type KeyResultInput,
  type MetricAggregation,
  type OkrDirection,
  type TeamOkr,
} from '../../api/okrs';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { DataState } from '../../components/DataState';
import { KeyResultProgress, OkrStatusChip, TaskList } from './OkrShared';
import { fieldToTarget, targetToField } from '../../components/formatters';

const FILTERS: { value: string; label: string }[] = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'changes_requested', label: 'Changes requested' },
  { value: 'approved', label: 'Approved' },
  { value: 'all', label: 'All' },
];

export function TeamOkrPage() {
  const [status, setStatus] = useState('submitted');
  const [reviewing, setReviewing] = useState<TeamOkr | null>(null);

  const teamQuery = useQuery({
    queryKey: ['team-okrs', status],
    queryFn: ({ signal }) => fetchTeamOkrs(status, signal).then((r) => r.okrs),
    refetchOnWindowFocus: false,
  });

  return (
    <Box>
      <PageHeader
        title="Team OKRs"
        subtitle="Review and approve the quarterly OKRs your direct reports have submitted."
        actions={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={status}
            onChange={(_, v) => {
              if (v) setStatus(v);
            }}
          >
            {FILTERS.map((f) => (
              <ToggleButton key={f.value} value={f.value}>
                {f.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        }
      />

      <DataState
        loading={teamQuery.isLoading}
        error={teamQuery.error}
        data={teamQuery.data}
        isEmpty={(d) => d.length === 0}
        emptyMessage="No OKRs match this filter. When your reports submit OKRs, they'll appear here for approval."
      >
        {(okrs) => (
          <Stack spacing={2}>
            {okrs.map((entry) => (
              <Card key={entry.okr.id} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ sm: 'center' }}
                    gap={1.5}
                    sx={{ mb: 2 }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar src={entry.owner?.picture ?? undefined} sx={{ width: 36, height: 36 }}>
                        {entry.owner?.name?.charAt(0) ?? '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {entry.owner?.name ?? 'Unknown user'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.owner?.email}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {quarterLabel(entry.okr.quarter)}
                      </Typography>
                      <OkrStatusChip status={entry.okr.status} />
                      <Button variant="contained" size="small" onClick={() => setReviewing(entry)}>
                        Review
                      </Button>
                    </Stack>
                  </Stack>

                  <Stack spacing={2}>
                    {entry.okr.keyResults.map((kr, i) => (
                      <Box key={`${entry.okr.id}-${i}`}>
                        <KeyResultProgress kr={kr} />
                        <TaskList kr={kr} />
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DataState>

      <ReviewDialog entry={reviewing} onClose={() => setReviewing(null)} />
    </Box>
  );
}

interface ReviewRow {
  layer: string;
  area: string;
  subarea: string;
  metric: string;
  label: string;
  unit: string;
  aggregation: MetricAggregation;
  direction: OkrDirection;
  target: string;
}

function ReviewDialog({ entry, onClose }: { entry: TeamOkr | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (entry) {
      setRows(
        entry.okr.keyResults.map((kr) => ({
          layer: kr.layer,
          area: kr.area,
          subarea: kr.subarea,
          metric: kr.metric,
          label: kr.label,
          unit: kr.unit,
          aggregation: kr.aggregation,
          direction: kr.direction,
          target: targetToField(kr.targetValue, kr.unit),
        })),
      );
      setNote('');
      setError(null);
    }
  }, [entry]);

  const toInput = (): KeyResultInput[] =>
    rows.map((r) => ({
      layer: r.layer,
      area: r.area,
      subarea: r.subarea,
      metric: r.metric,
      targetValue: fieldToTarget(r.target, r.unit),
      direction: r.direction,
    }));

  const targetsValid = rows.every((r) => {
    const n = Number(r.target);
    return r.target.trim() !== '' && Number.isFinite(n) && (r.direction === 'down' ? n >= 0 : n > 0);
  });

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['team-okrs'] });
    queryClient.invalidateQueries({ queryKey: ['my-okr'] });
    onClose();
  };

  const reviewMutation = useMutation({
    mutationFn: (action: 'approve' | 'request_changes') => {
      if (!entry) throw new Error('No OKR selected');
      return reviewOkr(
        entry.okr.id,
        action === 'approve'
          ? { action: 'approve', keyResults: toInput() }
          : { action: 'request_changes', note, keyResults: toInput() },
      );
    },
    onSuccess,
    onError: (err: unknown) => setError(err instanceof ApiError ? err.message : 'Review failed.'),
  });

  return (
    <Dialog open={!!entry} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Review OKRs — {entry?.owner?.name} ({entry ? quarterLabel(entry.okr.quarter) : ''})
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Adjust targets if needed, then approve or request changes. Requesting changes returns the plan to the owner to edit and resubmit.
        </Typography>

        <Stack divider={<Divider flexItem />} spacing={1.5}>
          {rows.map((r, i) => (
            <Box key={`${r.metric}-${i}`}>
              <Stack direction="row" gap={1.5} alignItems="center">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {r.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {[r.layer, r.area, AGGREGATION_LABEL[r.aggregation]].filter(Boolean).join(' · ')}
                    {r.direction === 'down' ? ' · lower is better' : ''}
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  label="Target"
                  type="number"
                  value={r.target}
                  helperText={AGGREGATION_LABEL[r.aggregation]}
                  onChange={(e) =>
                    setRows((prev) => prev.map((x, j) => (j === i ? { ...x, target: e.target.value } : x)))
                  }
                  InputProps={
                    r.unit === 'percent'
                      ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                      : undefined
                  }
                  sx={{ width: 150 }}
                />
              </Stack>
              {entry?.okr.keyResults[i] && <TaskList kr={entry.okr.keyResults[i]} />}
            </Box>
          ))}
        </Stack>

        <TextField
          label="Note to owner (required to request changes)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 3 }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={reviewMutation.isPending}>
          Cancel
        </Button>
        <Button
          color="warning"
          disabled={reviewMutation.isPending || note.trim() === '' || !targetsValid}
          onClick={() => reviewMutation.mutate('request_changes')}
        >
          Request changes
        </Button>
        <Button
          variant="contained"
          disabled={reviewMutation.isPending || !targetsValid}
          onClick={() => reviewMutation.mutate('approve')}
        >
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
}
